import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getEngagementAdminConfig,
  getEngagementAdminSummary,
  getEngagementDeliveryHistory,
  saveEngagementAdminConfig,
  resetEngagementAdminConfig,
  runEngagementAdminProcess,
  testEngagementAdminNotification,
} from '../services/engagementService';
import '../styles/engagementAdmin.css';

const MODULES = [
  ['family_setup', '👨‍👩‍👧', 'Aile kurulumu', 'Family setup'],
  ['family_invite', '💌', 'Üye daveti', 'Member invite'],
  ['shopping', '🛒', 'Alışveriş', 'Shopping'],
  ['calendar', '📅', 'Takvim', 'Calendar'],
  ['finance', '💰', 'Finans', 'Finance'],
  ['expenses', '🧾', 'Harcamalar', 'Expenses'],
  ['tasks', '✅', 'Görevler', 'Tasks'],
  ['health', '❤️', 'Sağlık', 'Health'],
  ['map', '📍', 'Aile haritası', 'Family map'],
  ['children', '👶', 'Çocuk takibi', 'Children'],
  ['chat', '💬', 'Aile sohbeti', 'Family chat'],
  ['documents', '📄', 'Belgeler', 'Documents'],
  ['cooking', '🍳', 'Bugün ne pişirsem', 'Cooking'],
];

const SEGMENTS = [
  ['family_setup_missing', 'Aile kurmamış kullanıcı', 'Missing family setup'],
  ['family_invite_missing', 'Üye davet etmemiş kullanıcı', 'Missing family invite'],
  ['module_viewed_no_data', 'Sayfaya girmiş ama veri eklememiş', 'Viewed module without data'],
  ['inactive_user', 'Pasif kullanıcı geri çağırma', 'Inactive user recall'],
  ['cross_module_discovery', 'Kullanılmayan güçlü modül tanıtımı', 'Cross module discovery'],
];

const SPAM_RULES = [
  ['skipIfActiveToday', 'Bugün aktif olan kullanıcıyı atla', 'Skip users active today'],
  ['pauseAfterTwoUnopened', 'Tepkisiz kullanıcıda frekansı düşür', 'Reduce frequency for no reaction'],
  ['requireActivePushDevice', 'Aktif push cihazı zorunlu', 'Require active push device'],
  ['respectUserNotificationSettings', 'Kullanıcı bildirim ayarlarına uy', 'Respect user settings'],
  ['allowInAppWithoutPush', 'Push yoksa in-app loglamaya izin ver', 'Allow in-app without push'],
];

function clone(obj) { return JSON.parse(JSON.stringify(obj || {})); }
function setDeep(obj, path, value) {
  const next = clone(obj);
  const parts = path.split('.');
  let cur = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    cur[parts[i]] = { ...(cur[parts[i]] || {}) };
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}
function Toggle({ checked, onChange, disabled }) {
  return <button type="button" disabled={disabled} onClick={() => onChange(!checked)} className={`engToggle ${checked ? 'on' : ''}`}><span /></button>;
}
function Num({ value, onChange, min = 0, max = 999 }) {
  return <input className="engInput" type="number" min={min} max={max} value={value ?? ''} onChange={(e) => onChange(Number(e.target.value || 0))} />;
}
function Time({ value, onChange }) {
  return <input className="engInput" type="time" value={value || '10:00'} onChange={(e) => onChange(e.target.value)} />;
}
function formatDate(value, tr) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString(tr ? 'tr-TR' : 'en-US');
}
const DELIVERY_STATUS = {
  sent: ['Push gönderildi', 'Push sent'],
  in_app_only: ['Uygulama içi', 'In-app only'],
  skipped: ['Atlandı', 'Skipped'],
  failed: ['Başarısız', 'Failed'],
  dry_run: ['Önizleme', 'Preview'],
  permission_denied: ['Yetki engeli', 'Permission blocked'],
};
const DELIVERY_SOURCE = {
  automatic: ['Otomatik', 'Automatic'],
  admin_manual: ['Admin manuel', 'Admin manual'],
  external_cron: ['Harici zamanlayıcı', 'External scheduler'],
  admin_test: ['Admin testi', 'Admin test'],
  preview: ['Önizleme', 'Preview'],
};
const REASON_LABELS = {
  active_today: ['Bugün aktif', 'Active today'],
  daily_limit: ['Günlük limit', 'Daily limit'],
  weekly_limit: ['Haftalık limit', 'Weekly limit'],
  min_gap: ['Minimum süre', 'Minimum gap'],
  module_cooldown: ['Modül bekleme süresi', 'Module cooldown'],
  no_reaction_cooldown: ['Tepkisizlik beklemesi', 'No-reaction cooldown'],
  no_active_push_device: ['Aktif push cihazı yok', 'No active push device'],
  notification_settings_disabled: ['Bildirim ayarı kapalı', 'Notification setting disabled'],
  permission_denied: ['Sayfa yetkisi yok', 'Page permission denied'],
  segment_disabled: ['Segment kapalı', 'Segment disabled'],
  module_disabled: ['Modül kapalı', 'Module disabled'],
  in_app_without_push_disabled: ['Push olmadan in-app kapalı', 'In-app without push disabled'],
  unspecified: ['Belirtilmemiş', 'Unspecified'],
};

function pairLabel(map, key, tr) {
  const pair = map[key];
  return pair ? pair[tr ? 0 : 1] : (key || '—');
}
function maskEmail(value) {
  const [name, domain] = String(value || '').split('@');
  if (!domain) return '';
  const visible = name.slice(0, Math.min(2, name.length));
  return visible + '•••@' + domain;
}
function DeliveryHistory({ tr }) {
  const [windowKey, setWindowKey] = useState('24h');
  const [filters, setFilters] = useState({ status: '', moduleKey: '', segment: '', source: '', search: '' });
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState('');

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await getEngagementDeliveryHistory({ windowKey, ...filters, limit: 100 });
        if (active) { setHistory(result); setError(''); }
      } catch (err) {
        if (active) setError(err?.message || (tr ? 'Gönderim geçmişi yüklenemedi.' : 'Delivery history could not be loaded.'));
      } finally {
        if (active) setLoading(false);
      }
    }, filters.search ? 350 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [windowKey, filters, tr]);

  const summary = history?.summary || {};
  const items = Array.isArray(history?.items) ? history.items : [];
  const modules = history?.filters?.modules || [];
  const segments = history?.filters?.segments || [];
  const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const moduleName = (key) => {
    const item = MODULES.find(([moduleKey]) => moduleKey === key);
    return item ? item[tr ? 2 : 3] : (key || '—');
  };

  return <section className="engSection engDeliverySection">
    <div className="engSectionHead engDeliveryHead">
      <div>
        <span>{tr ? 'DENETİM VE PERFORMANS' : 'AUDIT AND PERFORMANCE'}</span>
        <h3>{tr ? 'Gönderilen bildirimler' : 'Notification delivery history'}</h3>
        <p>{tr ? 'Son 100 işlem, alıcılar ve otomasyon sonuçları' : 'Last 100 actions, recipients and automation outcomes'}</p>
      </div>
      <div className="engPeriodTabs" role="tablist">
        {[['24h', tr ? '24 saat' : '24 hours'], ['7d', tr ? '7 gün' : '7 days'], ['30d', tr ? '30 gün' : '30 days']].map(([key, label]) =>
          <button type="button" key={key} className={windowKey === key ? 'active' : ''} onClick={() => setWindowKey(key)}>{label}</button>)}
      </div>
    </div>

    <div className="engDeliveryStats">
      {[
        [summary.pushSent, tr ? 'Başarılı push' : 'Push sent', 'success'],
        [summary.inAppOnly, tr ? 'Yalnızca uygulama içi' : 'In-app only', 'info'],
        [summary.uniqueRecipients, tr ? 'Benzersiz alıcı' : 'Unique recipients', 'primary'],
        [summary.returnedUsers, tr ? 'Uygulamaya dönen' : 'Returned to app', 'success'],
        [(summary.returnRate || 0) + '%', tr ? 'Geri dönüş oranı' : 'Return rate', 'primary'],
        [summary.skipped, tr ? 'Atlanan' : 'Skipped', 'warning'],
        [summary.failed, tr ? 'Başarısız' : 'Failed', 'danger'],
        [summary.previews, tr ? 'Önizleme' : 'Previews', 'neutral'],
      ].map(([value, label, tone]) => <div className={'engDeliveryStat ' + tone} key={label}><strong>{value ?? 0}</strong><span>{label}</span></div>)}
    </div>

    <div className="engDeliveryFilters">
      <input value={filters.search} onChange={(e) => update('search', e.target.value)} placeholder={tr ? 'Kullanıcı, e-posta veya başlık ara' : 'Search user, email or title'} />
      <select value={filters.status} onChange={(e) => update('status', e.target.value)}>
        <option value="">{tr ? 'Tüm durumlar' : 'All statuses'}</option>
        {Object.keys(DELIVERY_STATUS).map((key) => <option key={key} value={key}>{pairLabel(DELIVERY_STATUS, key, tr)}</option>)}
      </select>
      <select value={filters.moduleKey} onChange={(e) => update('moduleKey', e.target.value)}>
        <option value="">{tr ? 'Tüm modüller' : 'All modules'}</option>
        {modules.map((key) => <option key={key} value={key}>{moduleName(key)}</option>)}
      </select>
      <select value={filters.segment} onChange={(e) => update('segment', e.target.value)}>
        <option value="">{tr ? 'Tüm segmentler' : 'All segments'}</option>
        {segments.map((key) => <option key={key} value={key}>{key}</option>)}
      </select>
      <select value={filters.source} onChange={(e) => update('source', e.target.value)}>
        <option value="">{tr ? 'Tüm kaynaklar' : 'All sources'}</option>
        {Object.keys(DELIVERY_SOURCE).map((key) => <option key={key} value={key}>{pairLabel(DELIVERY_SOURCE, key, tr)}</option>)}
      </select>
      <button type="button" onClick={() => setFilters({ status: '', moduleKey: '', segment: '', source: '', search: '' })}>{tr ? 'Filtreleri temizle' : 'Clear filters'}</button>
    </div>

    {!!history?.reasons?.length && <div className="engReasonStrip">
      <strong>{tr ? 'Başlıca atlanma nedenleri' : 'Top skip reasons'}</strong>
      <div>{history.reasons.map((item) => <span key={item.reason}>{pairLabel(REASON_LABELS, item.reason, tr)} <b>{item.count}</b></span>)}</div>
    </div>}

    {error && <div className="engStatus engStatusError">{error}</div>}
    <div className={'engDeliveryList ' + (loading ? 'isLoading' : '')}>
      {loading && !history ? <div className="engEmpty">{tr ? 'Gönderim geçmişi yükleniyor...' : 'Loading delivery history...'}</div> : null}
      {!loading && !items.length ? <div className="engEmpty">{tr ? 'Bu filtrelere uygun kayıt yok.' : 'No records match these filters.'}</div> : null}
      {items.map((item) => {
        const source = item.payload?.source || 'automatic';
        const isOpen = expanded === item.id;
        const returned = !!item.returned_at;
        return <button type="button" className={'engDeliveryRow status-' + item.status} key={item.id} onClick={() => setExpanded(isOpen ? '' : item.id)}>
          <div className="engDeliveryIdentity">
            <span className="engRecipientAvatar">{String(item.display_name || '?').trim().charAt(0).toUpperCase()}</span>
            <div><strong>{item.display_name || (tr ? 'Silinmiş kullanıcı' : 'Deleted user')}</strong><small>{maskEmail(item.email)}</small></div>
          </div>
          <div className="engDeliveryMessage">
            <strong>{item.title || '—'}</strong>
            <p className={isOpen ? 'expanded' : ''}>{item.body || item.reason || '—'}</p>
            <div><span>{moduleName(item.module_key)}</span><span>{item.segment || '—'}</span><span>{pairLabel(DELIVERY_SOURCE, source, tr)}</span></div>
          </div>
          <div className="engDeliveryMeta">
            <span className={'engStatusBadge ' + item.status}>{pairLabel(DELIVERY_STATUS, item.status, tr)}</span>
            <time>{formatDate(item.created_at, tr)}</time>
            {returned && <small className="returned">{tr ? 'Uygulamaya döndü' : 'Returned to app'}</small>}
            {Number(item.payload?.failed || 0) > 0 && <small className="failed">{Number(item.payload.failed)} {tr ? 'cihaz başarısız' : 'device failures'}</small>}
            {item.reason && <small>{pairLabel(REASON_LABELS, item.reason, tr)}</small>}
          </div>
        </button>;
      })}
    </div>
    <div className="engDeliveryFoot">
      <span>{tr ? 'En fazla son ' + (history?.limit || 100) + ' kayıt gösterilir' : 'Up to the latest ' + (history?.limit || 100) + ' records are shown'}</span>
      <button type="button" onClick={() => setFilters((prev) => ({ ...prev }))} disabled={loading}>{loading ? (tr ? 'Yenileniyor...' : 'Refreshing...') : (tr ? 'Yenile' : 'Refresh')}</button>
    </div>
  </section>;
}

export default function EngagementAdminPanel({ compact = false, tr = true }) {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [cfg, sum] = await Promise.all([getEngagementAdminConfig(), getEngagementAdminSummary()]);
      setConfig(cfg || {}); setSummary(sum || {}); setStatus('');
    } catch (e) { setStatus((tr ? 'Yüklenemedi: ' : 'Could not load: ') + (e.message || e)); }
    finally { setLoading(false); }
  }, [tr]);
  useEffect(() => { load(); }, [load]);
  const set = (path, value) => setConfig((prev) => setDeep(prev || {}, path, value));
  const save = async () => { try { setSaving(true); const cfg = await saveEngagementAdminConfig(config); setConfig(cfg); setStatus(tr ? 'Ayarlar kaydedildi.' : 'Settings saved.'); } catch (e) { setStatus(e.message || String(e)); } finally { setSaving(false); } };
  const reset = async () => { try { setSaving(true); const cfg = await resetEngagementAdminConfig(); setConfig(cfg); setStatus(tr ? 'Varsayılan ayarlar yüklendi ve kaydedildi.' : 'Defaults restored and saved.'); } catch (e) { setStatus(e.message || String(e)); } finally { setSaving(false); } };
  const dryRun = async () => {
    try {
      setRunning(true);
      const r = await runEngagementAdminProcess({ dryRun: true, limit: 25 });
      setStatus(`${tr ? 'Dry-run test' : 'Dry-run test'}: ${r?.candidateCount ?? 0} ${tr ? 'aday bulundu' : 'candidates found'}, ${r?.processed ?? 0} ${tr ? 'işlendi' : 'processed'}.`);
      const sum = await getEngagementAdminSummary();
      setSummary(sum);
    } catch (e) { setStatus(e.message || String(e)); }
    finally { setRunning(false); }
  };
  const liveRun = async () => {
    const ok = window.confirm(tr
      ? 'Zamanı gelen kullanıcı hatırlatmaları şimdi çalıştırılacak. Günlük/haftalık limitler, sessiz saatler ve cooldown kuralları uygulanır. Devam edilsin mi?'
      : 'Due user reminders will run now. Daily/weekly limits, quiet hours and cooldown rules will be applied. Continue?');
    if (!ok) return;
    try {
      setRunning(true);
      const r = await runEngagementAdminProcess({ dryRun: false, limit: 25 });
      setStatus(`${tr ? 'Zamanı gelenler kontrol edildi' : 'Due reminders checked'}: ${r?.processed ?? 0} ${tr ? 'işlendi' : 'processed'}, ${r?.sent ?? 0} ${tr ? 'gönderim/işlem' : 'sent/actions'}, ${r?.skipped ?? 0} ${tr ? 'atlandı' : 'skipped'}.`);
      const sum = await getEngagementAdminSummary();
      setSummary(sum);
    } catch (e) { setStatus(e.message || String(e)); }
    finally { setRunning(false); }
  };
  const sendTest = async () => {
    try {
      setTesting(true);
      const r = await testEngagementAdminNotification();
      setStatus(r?.ok
        ? `${tr ? 'Test bildirimi gönderildi' : 'Test notification sent'}: ${r?.status || 'ok'}${Number(r?.sent || 0) > 0 ? ` · push ${r.sent}` : ''}`
        : (r?.message || (tr ? 'Test bildirimi gönderilemedi.' : 'Test notification could not be sent.')));
      const sum = await getEngagementAdminSummary();
      setSummary(sum);
    } catch (e) { setStatus(e.message || String(e)); }
    finally { setTesting(false); }
  };

  const moduleRows = useMemo(() => new Map((summary?.modules || []).map((m) => [m.module_key, m])), [summary]);
  const scheduler = summary?.scheduler || {};
  const schedulerHealthy = !!scheduler.startedAt && !scheduler.lastError;
  if (loading) return <div className="engPanel engLoading">{tr ? 'Kullanıcı etkileşim merkezi yükleniyor...' : 'Loading engagement center...'}</div>;
  if (!config) return <div className="engPanel engLoading danger">{status || (tr ? 'Ayarlar yüklenemedi.' : 'Settings could not be loaded.')}</div>;

  return <div className={`engPanel ${compact ? 'compact' : ''}`}>
    <div className="engHero">
      <div><span>{tr ? 'Kullanıcı Hatırlatma Merkezi' : 'User Reminder Center'}</span><h2>{tr ? 'Engagement Bildirim Yönetimi' : 'Engagement Notification Management'}</h2><p>{tr ? 'Kayıt, aile daveti, modül kullanımı ve pasif kullanıcı hatırlatmaları tek merkezden yönetilir.' : 'Manage signup, family invite, module adoption and inactive user reminders from one place.'}</p></div>
      <Toggle checked={!!config.enabled} onChange={(v) => set('enabled', v)} />
    </div>
    <div className="engStatsGrid">
      <div><strong>{summary?.users?.total ?? 0}</strong><span>{tr ? 'Takip edilen kullanıcı' : 'Tracked users'}</span></div>
      <div><strong>{summary?.users?.active_today ?? 0}</strong><span>{tr ? 'Bugün aktif' : 'Active today'}</span></div>
      <div><strong>{config.dryRun ? (tr ? 'Dry-run' : 'Dry run') : (tr ? 'Canlı' : 'Live')}</strong><span>{tr ? 'Gönderim modu' : 'Send mode'}</span></div>
      <div><strong>{config.cronEnabled && schedulerHealthy ? (tr ? 'Otomatik' : 'Automatic') : (tr ? 'Kapalı' : 'Off')}</strong><span>{tr ? 'Zamanlayıcı' : 'Scheduler'}</span></div>
    </div>

    <div className="engActions"><button onClick={save} disabled={saving}>{saving ? (tr ? 'Kaydediliyor...' : 'Saving...') : (tr ? 'Kaydet' : 'Save')}</button><button onClick={reset} disabled={saving}>{tr ? 'Varsayılana dön' : 'Restore defaults'}</button><button onClick={dryRun} disabled={running}>{tr ? 'Göndermeden kontrol et' : 'Preview without sending'}</button><button onClick={liveRun} disabled={running}>{tr ? 'Şimdi kontrol et ve gönder' : 'Check and send now'}</button><button onClick={sendTest} disabled={testing}>{testing ? (tr ? 'Test gönderiliyor...' : 'Sending test...') : (tr ? 'Kendime test gönder' : 'Send test to me')}</button></div>
    <div className="engCronInfo">
      <strong>{tr ? 'Otomatik gönderim durumu' : 'Automatic delivery status'}</strong>
      <span>{tr ? 'Backend, zamanı gelen hatırlatmaları 15 dakikada bir otomatik kontrol eder. Harici cron kurulumu gerekmez.' : 'The backend automatically checks due reminders every 15 minutes. No external cron setup is required.'}</span>
      <div className={'engSchedulerState ' + (schedulerHealthy ? 'isHealthy' : 'isWaiting')}>
        <i aria-hidden="true" />
        <span>{schedulerHealthy
          ? (tr ? 'Zamanlayıcı çalışıyor' : 'Scheduler is running') + (scheduler.lastCompletedAt ? ' · ' + (tr ? 'Son kontrol' : 'Last check') + ': ' + formatDate(scheduler.lastCompletedAt, tr) : '')
          : (tr ? 'Zamanlayıcı ilk kontrolü bekliyor veya henüz durum bildirmedi' : 'Scheduler is waiting for its first check or has not reported status yet')}
        </span>
      </div>
      <em>{tr ? '“Göndermeden kontrol et” bildirim göndermez ve limitleri tüketmez. “Şimdi kontrol et ve gönder” aynı korumalı işlemi hemen başlatır; normalde kullanmanız gerekmez.' : '“Preview without sending” sends nothing and consumes no limits. “Check and send now” starts the same protected process immediately; normally you do not need to use it.'}</em>
    </div>
    {status && <div className="engStatus">{status}</div>}

    <section className="engSection"><div className="engSectionHead"><h3>{tr ? 'Genel çalışma ayarları' : 'Global rules'}</h3><Toggle checked={!!config.cronEnabled} onChange={(v) => set('cronEnabled', v)} /></div>
      <div className="engFormGrid">
        <label><span>{tr ? 'Dry-run modu' : 'Dry-run mode'}</span><Toggle checked={!!config.dryRun} onChange={(v) => set('dryRun', v)} /></label>
        <label><span>{tr ? 'İlk hatırlatma gecikmesi/saat' : 'First reminder delay/hours'}</span><Num value={config.firstReminderDelayHours} onChange={(v) => set('firstReminderDelayHours', v)} min={1} max={240} /></label>
        <label><span>{tr ? 'Pasif kullanıcı günü' : 'Inactive user days'}</span><Num value={config.inactiveUserDays} onChange={(v) => set('inactiveUserDays', v)} min={1} max={180} /></label>
        <label><span>{tr ? 'Günlük limit' : 'Daily limit'}</span><Num value={config.dailyLimit} onChange={(v) => set('dailyLimit', v)} min={1} max={10} /></label>
        <label><span>{tr ? 'Haftalık limit' : 'Weekly limit'}</span><Num value={config.weeklyLimit} onChange={(v) => set('weeklyLimit', v)} min={1} max={30} /></label>
        <label><span>{tr ? 'Modül cooldown/gün' : 'Module cooldown/days'}</span><Num value={config.moduleCooldownDays} onChange={(v) => set('moduleCooldownDays', v)} min={1} max={60} /></label>
        <label><span>{tr ? 'Sessiz başlangıç' : 'Quiet start'}</span><Time value={config.quietHours?.start} onChange={(v) => set('quietHours.start', v)} /></label>
        <label><span>{tr ? 'Sessiz bitiş' : 'Quiet end'}</span><Time value={config.quietHours?.end} onChange={(v) => set('quietHours.end', v)} /></label>
        <label><span>{tr ? 'Gönderim başlangıç' : 'Send start'}</span><Time value={config.sendWindow?.start} onChange={(v) => set('sendWindow.start', v)} /></label>
        <label><span>{tr ? 'Gönderim bitiş' : 'Send end'}</span><Time value={config.sendWindow?.end} onChange={(v) => set('sendWindow.end', v)} /></label>
      </div>
      <p className="engStatus">{tr ? 'Tüm saatler alıcının yerel saatine göre uygulanır; saat dilimi bilinmiyorsa Europe/Istanbul kullanılır.' : 'All times use the recipient’s local time; Europe/Istanbul is used when the time zone is unavailable.'}</p>
    </section>

    <section className="engSection"><div className="engSectionHead"><h3>{tr ? 'Segmentler' : 'Segments'}</h3><span>{tr ? 'Tamamı dinamik' : 'Fully dynamic'}</span></div><div className="engToggleList">{SEGMENTS.map(([key, trLabel, enLabel]) => <div key={key}><span>{tr ? trLabel : enLabel}</span><Toggle checked={config.segments?.[key] !== false} onChange={(v) => set(`segments.${key}`, v)} /></div>)}</div></section>

    <section className="engSection"><div className="engSectionHead"><h3>{tr ? 'Modül bildirimleri' : 'Module notifications'}</h3><span>{tr ? '5 kayıt görünür, fazlası kayar' : '5 visible, rest scrolls'}</span></div><div className="engModuleList">{MODULES.map(([key, icon, trLabel, enLabel]) => { const m = moduleRows.get(key) || {}; return <div key={key} className="engModuleRow"><b>{icon}</b><div><strong>{tr ? trLabel : enLabel}</strong><span>{tr ? 'Giren' : 'Viewed'}: {m.users || 0} · {tr ? 'Veri ekleyen' : 'With data'}: {m.users_with_data || 0}</span></div><Toggle checked={config.modules?.[key] !== false} onChange={(v) => set(`modules.${key}`, v)} /></div>; })}</div></section>

    <section className="engSection"><div className="engSectionHead"><h3>{tr ? 'Anti-spam kuralları' : 'Anti-spam rules'}</h3><span>{tr ? 'Dinamik değiştirilebilir' : 'Dynamically editable'}</span></div><div className="engToggleList">{SPAM_RULES.map(([key, trLabel, enLabel]) => <div key={key}><span>{tr ? trLabel : enLabel}</span><Toggle checked={config.antiSpam?.[key] !== false} onChange={(v) => set(`antiSpam.${key}`, v)} /></div>)}<label className="engInline"><span>{tr ? 'Tepkisizlik sonrası cooldown/gün' : 'No reaction cooldown/days'}</span><Num value={config.noReactionCooldownDays} onChange={(v) => set('noReactionCooldownDays', v)} min={1} max={120} /></label></div></section>

    <DeliveryHistory tr={tr} />
  </div>;
}
